import { type IFramework } from '@/framework';
import {
    LayerType,
    type ILayerProps,
    type IVectorLayerProps,
    type TextContentPadding,
    type VectorTextInfo,
} from '@/version-types';
import { pushHistory, requestVectorSVGPreview, resolveVectorTextLayout, updateLayer } from '../utils';

export interface ITextPadding {
    /**
     * 设置文本颜色
     *
     * @param layerID - 图层 ID
     * @param padding - 预留 padding
     * @param options - 可选；配置选项；`options.writeHistory` 是否写入历史记录
     */
    setTextPadding(
        layerID: string,
        padding: TextContentPadding,
        options?: { writeHistory: boolean },
    ): ILayerProps | void;
}

export class TextPadding implements ITextPadding {
    private prevLayer: ILayerProps | null = null;

    constructor(private framework: IFramework) {}

    /**
     * 合并 padding 并用相同候选原子解析最终 Vector 几何。
     *
     * @param layerID - 目标 Vector Layer ID。
     * @param padding - 本轮覆盖的 padding 字段。
     * @param options - 是否结束连续调节并写入历史。
     * @returns 更新后的 Vector；输入不适用时返回 void。
     */
    setTextPadding(layerID: string, padding: TextContentPadding, options = { writeHistory: false }) {
        const prevLayer = (() => {
            if (this.prevLayer) return this.prevLayer;
            this.prevLayer = this.framework.service.flatLayer.getLayer(layerID, true);
            return this.prevLayer;
        })();

        if (!prevLayer || prevLayer.layerType !== LayerType.VECTOR_LAYER) return;

        const layerData = prevLayer.data as IVectorLayerProps;
        const { textInfo } = layerData;

        if (!textInfo) return;

        const { writeHistory } = options;

        if (writeHistory) {
            const nextLayer = this.framework.service.flatLayer.getLayer(layerID, true);

            requestVectorSVGPreview(this.framework, 'commit', nextLayer);
            updateLayer(this.framework, nextLayer);
            pushHistory(this.framework, prevLayer, nextLayer);
            this.prevLayer = null;

            return nextLayer;
        }

        let prevPadding = textInfo.padding;
        if (!prevPadding) {
            prevPadding = { top: 0, right: 0, bottom: 0, left: 0 };
        }

        const nextPadding: TextContentPadding = { ...prevPadding, ...padding };

        const nextTextInfo: VectorTextInfo = { ...textInfo, padding: nextPadding };
        const nextLayerData: IVectorLayerProps = { ...layerData, textInfo: nextTextInfo };
        const nextLayer = resolveVectorTextLayout(this.framework, { ...prevLayer, data: nextLayerData });

        requestVectorSVGPreview(this.framework, 'update', nextLayer);
        updateLayer(this.framework, nextLayer, { dataOnly: true });

        return nextLayer;
    }
}
